/**
 * What a file picker offers, in one place.
 *
 * The bug this exists to prevent: for a long time every picker said
 * accept="image/*" while the server accepted a shorter list, so someone could
 * choose an iPhone photo, wait for it to upload, and only then be told no.
 * What is offered here and what the server accepts (backend/upload_ingest.py)
 * are one decision, written down twice, so they have to be changed together.
 *
 * The extensions are listed as well as the types because desktop file dialogs
 * treat a bare image/* inconsistently, and naming .heic explicitly is what
 * makes an iPhone hand over the original photo rather than a converted copy.
 */

import { apiErr } from "./api";

// Receipts and other evidence: photos from any phone, or a PDF.
// Open-ended, and that is a REVERSAL of how this started.
//
// It began as a fixed list, on the reasoning that the receipt routes refuse
// what they cannot read so the picker should match them exactly. That was true
// then. It stopped being true when the server's rule became "anything we can
// actually open" — after which a fixed list here could only ever offer LESS
// than the server takes, and three separate review rounds found three more
// formats it was hiding.
//
// Perfect agreement is not available: the operating system's idea of an image
// and the server's do not match, and never will. So the question is which
// mismatch is kinder. A picker that offers something the server refuses costs
// you one upload and a message that says why. A picker that HIDES something
// the server accepts gives you no message at all and no way to find out. The
// second is worse, so this errs open.
//
// The extensions are still named because some desktops have no mapping for
// them and would grey them out even under image/*.
export const RECEIPT_ACCEPT =
  "image/*,application/pdf,.heic,.heif,.avif,.jp2,.j2k,.tif,.tiff,.bmp,.pdf";

// Every surface says the same thing, in the same words.
export const RECEIPT_HINT = "iPhone or Android photos (HEIC, JPEG, PNG, WEBP, AVIF) or a PDF — up to 25 MB";

// Shorter form, for the surfaces where the line has to fit beside a button.
export const RECEIPT_HINT_SHORT = "Phone photo (iPhone included) or PDF";

// Documents that are legitimately spreadsheets and Word files as well as
// photos: cost-of-attendance evidence, plan statements, expense paperwork.
//
// `image/*` stays in this one deliberately. The routes behind it store
// whatever they are given, so a finite list here would be the picker offering
// LESS than the server accepts — the same mismatch this file exists to
// prevent, just pointing the other way. Anything the operating system calls
// an image can still be chosen, exactly as before.
//
// RECEIPT_ACCEPT above is finite on purpose: the routes behind THAT one refuse
// what they cannot read, so the picker has to match them exactly.
export const DOCUMENT_ACCEPT = `image/*,${RECEIPT_ACCEPT},.doc,.docx,.xlsx,.xls,.csv`;

// The extensions a picker will hand back for the formats above. Used by the
// surfaces that filter the dropped files themselves instead of trusting the
// dialog — a filter narrower than the accept string silently swallows files.
export const RECEIPT_EXTENSIONS = [
  "jpg", "jpeg", "png", "webp", "gif", "heic", "heif", "avif", "tif", "tiff",
  // Scanner formats. The server takes anything it can actually open, so a
  // chooser that hides these is offering less than the app accepts.
  "bmp", "jp2", "j2k", "pdf",
];

export const DOCUMENT_EXTENSIONS = [
  ...RECEIPT_EXTENSIONS, "doc", "docx", "xls", "xlsx", "csv",
];

// Deliberately generous, for the same reason the accept string is. The server
// decides what it can read and says so in words; a stricter guess here can
// only discard something it would have taken. The one thing worth stopping in
// the browser is a video, because those are large and certain to be refused.
const VIDEO_EXTENSIONS = ["mov", "mp4", "m4v", "avi", "mkv", "webm", "3gp", "hevc"];

export function hasAcceptedExtension(filename) {
  const ext = (filename || "").split(".").pop().toLowerCase();
  return !VIDEO_EXTENSIONS.includes(ext);
}

// A document attached on the expense form goes through the inbox's own door, so
// one reader reads it and the per-document total check measures it (ZEN-724).
// A file the household already holds is cited, not stored twice (her ruling,
// 24 Oct 2026). With an expense id it is attached there; without one the caller
// passes the returned id in the save's receipt_ids.
export async function sendExpenseDocument(api, file, studentId, expenseId) {
  const fd = new FormData();
  fd.append("file", file);
  if (studentId) fd.append("student_id", studentId);
  if (expenseId) fd.append("expense_id", expenseId);
  fd.append("run_ai", "true");
  fd.append("reuse_held", "true");
  const { data } = await api.post("/receipts/upload", fd);
  return data;
}

// Take documents a failed save just added back out of the inbox (ZEN-724).
// The inbox's own Remove: hidden, never erased, and refused for one an expense
// uses. Best effort: a document left behind waits in the inbox, nothing is lost.
export async function takeBackFromInbox(api, ids) {
  for (const id of ids || []) {
    try { await api.post(`/receipts/${id}/remove`); } catch { /* left in the inbox */ }
  }
}

// For surfaces whose route stores whatever it is given. A drop filter narrower
// than its own picker throws the file away without a word, which is the worst
// of the three ways this can be wrong.
export function hasAcceptedDocumentExtension(filename) {
  return hasAcceptedExtension(filename);
}

// ─── What a failed upload tells the person, in one place (ZEN-125) ─────────
//
// The import page's catch block used to show the hardcoded words "Upload
// failed" for every non-duplicate failure — the server's actual reason was
// thrown away — and it stored the queue item as a DUPLICATE regardless of
// what happened (only the toast text branched on the status code). A family
// could not tell a refused upload from a duplicate one, or act on either.
// These two helpers are that decision written down once, where a test can
// hold it still.


// Three-way branch, keyed on whether the server ANSWERED at all:
//   409           -> "duplicate", with the server's message (it names the
//                    original file and date);
//   any response  -> "failed", with the server's detail when it sent one,
//                    else a generic refusal line carrying the status code
//                    (a proxy's plain-text 500 is a refusal, not a network
//                    problem — do not claim the upload "did not reach" a
//                    server that plainly answered);
//   no response   -> "failed", said as a connection problem.
// errorMsg is ALWAYS a plain string — detail extraction goes through apiErr,
// which flattens the structured shapes a 422 can carry — because this value
// is rendered straight into the card.
export function uploadFailureState(error) {
  const response = error?.response;
  if (response?.status === 409) {
    return { status: "duplicate", errorMsg: apiErr(error) };
  }
  if (response) {
    const hasDetail = response.data != null && response.data.detail != null;
    return {
      status: "failed",
      errorMsg: hasDetail
        ? apiErr(error)
        : `The server refused this upload (status ${response.status}).`,
    };
  }
  return {
    status: "failed",
    errorMsg: "This upload did not reach the server — check your connection and try again.",
  };
}

// Card tone + message per queue status. The statuses that existed before
// ZEN-125 keep exactly the presentation they had; "failed" is new and reads
// as an error, and a status nobody recognises is NEVER shown as success —
// the old rendering fell through to the green "ready" style for anything it
// did not know, which is how a failure wore a success card.
export const QUEUE_TONES = {
  processing: "border-[#D97706]/40 bg-[#FEF3C7]/30",
  error: "border-[#F01919]/30 bg-[#F01919]/5",
  duplicate: "border-[#D97706]/40 bg-[#FEF3C7]/20",
  failed: "border-[#F01919]/40 bg-[#F01919]/10",
  ready: "border-[#077A39]/30 bg-[#077A39]/5",
};

export function queueItemPresentation(item) {
  const status = item?.status;
  if (status === "processing") {
    return { kind: "processing", tone: QUEUE_TONES.processing, message: "Reading receipt…" };
  }
  if (status === "ready") {
    return { kind: "ready", tone: QUEUE_TONES.ready, message: null };
  }
  if (status === "error") {
    return { kind: "error", tone: QUEUE_TONES.error,
             message: item.errorMsg || "Couldn't read it — enter the details in review" };
  }
  if (status === "duplicate") {
    return { kind: "duplicate", tone: QUEUE_TONES.duplicate,
             message: item.errorMsg || "Duplicate — already uploaded" };
  }
  if (status === "failed") {
    return { kind: "failed", tone: QUEUE_TONES.failed,
             message: item.errorMsg || "This file was not uploaded." };
  }
  return { kind: "failed", tone: QUEUE_TONES.failed,
           message: item?.errorMsg || "Something went wrong with this file." };
}

// A READING THAT FOUND NOTHING SAYS SO. (ZEN-185)
// The reader ran and answered but found neither a date nor an amount, the two
// things every real receipt carries. Without this the card looked like any
// other draft, so a photo of several receipts came back blank with no reason.
// A failed reading (ai_error) has its own notice; no reading at all (draft is
// null) is not this case. Every screen that shows a reading asks this rule.
const isBlank = (v) => v === null || v === undefined || (typeof v === "string" && v.trim() === "");

// THE AMOUNT THE READER FOUND, OR NONE. An expense is more than $0 (Maryse,
// 1 Sep 2026), so a reading of 0 is the reader failing to find an amount, not
// an amount: "When the reader cannot read the amount it simply says so, not
// enter $0" (Maryse, in chat, 30 Oct 2026). Every screen that shows or
// pre-fills a read amount goes through here, so $0.00 is never shown as found.
export function readAmount(draft) {
  const v = draft && typeof draft === "object" ? draft.original_amount : null;
  if (isBlank(v)) return null;
  const n = Number(v);
  if (!Number.isFinite(n) || n === 0) return null;
  return v;
}

export function readingFoundNothing(receipt) {
  if (!receipt || receipt.ai_error) return false;
  const d = receipt.draft;
  if (!d || typeof d !== "object") return false;
  return isBlank(d.transaction_date) && readAmount(d) === null;
}

// Wording approved by Maryse in chat, 30 Oct 2026 (ZEN-185).
export const READING_FOUND_NOTHING =
  "We couldn't find a date or an amount on this photo, so the details are blank. If the photo shows more than one receipt, take a separate photo of each one instead.";
